import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ScrollRestoration | null} */
  #previousScrollRestoration = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#syncTriggerAriaExpanded);
    this.addEventListener(DialogCloseEvent.eventName, this.#syncTriggerAriaExpanded);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#syncTriggerAriaExpanded);
    this.removeEventListener(DialogCloseEvent.eventName, this.#syncTriggerAriaExpanded);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    // The body is scroll-locked via `position: fixed; top: -scrollY` while the
    // drawer is open, so the document scroll is 0 at this point. The synthetic
    // history entry would therefore record a ~0 scroll position, and the
    // browser's automatic scroll restoration would snap the page to the top on
    // `history.back()` (overriding the scrollTo() that dialog.js performs).
    // Disable auto restoration for the drawer's lifecycle so dialog.js wins.
    if ('scrollRestoration' in history) {
      this.#previousScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
    }

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
    this.#restoreScrollRestoration();
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
    this.#restoreScrollRestoration();
  };

  #restoreScrollRestoration = () => {
    if ('scrollRestoration' in history && this.#previousScrollRestoration) {
      history.scrollRestoration = this.#previousScrollRestoration;
      this.#previousScrollRestoration = null;
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   *
   * Horizon 4.x replaced 3.x's dedicated CartAddEvent with a single
   * cartLinesUpdate event covering add/update/remove, so the add is
   * identified by `action` and the resulting item count arrives on
   * `event.promise` rather than synchronously on the event.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartAdd = (event) => {
    if (event.action !== 'add') return;

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.#announceCartCount(detail?.itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[gbk-cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * gbk-header binds `on:click="#cart-drawer/toggle"`, which was written
   * against the `<theme-drawer>` this component replaces. Keeping the method
   * name means the header needs no change.
   */
  toggle() {
    this.toggleDialog();
  }

  /**
   * Horizon's header-actions keeps the trigger's `aria-expanded` in step by
   * listening for the theme-drawer's open/close events, which this drawer no
   * longer fires. gbk-header replaces that snippet anyway, so the sync is
   * done here instead of leaving the trigger permanently reading "collapsed".
   */
  #syncTriggerAriaExpanded = () => {
    const expanded = this.refs.dialog?.open ? 'true' : 'false';
    for (const trigger of document.querySelectorAll('[aria-controls="cart-drawer"]')) {
      trigger.setAttribute('aria-expanded', expanded);
    }
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
